import * as _ from 'lodash';
import * as debug from 'debug';

import { ElementRef, Injectable } from '@angular/core';
import { CrudApiProvider } from '../crud-api/crud-api';
import { ConfigProvider } from '../config/config';
import { UserProvider } from '../user/user';
import { ToolsProvider } from '../tools/tools';

@Injectable()
export class TaggingProvider {
  // the general idea here was to move this all to this provider as a first
  // step in migrating the tagging feature to an isolated directive of its own
  // I didn't have enough time, but it should be fairly straight forward conversion

  log = debug('app:TaggingProvider');

  // typing & selecting
  toggle = false;
  text = '';
  taggedUsers: any[] = [];
  inputElement: Element = null;
  lastTargetInput:
    | EventTarget
    | {
        value: string;
        focus: () => {};
        blur: () => {};
        querySelector: () => {};
      };
  // search
  isSearching = true;
  searchText = '';
  searchResults: {
    people?: any[];
    peopleMax?: number;
  } = {
    people: [],
    peopleMax: 10,
  };
  searchId = '';

  constructor(
    public crudApi: CrudApiProvider,
    public config: ConfigProvider,
    public user: UserProvider,
    public tools: ToolsProvider
  ) {
    setTimeout(() => {
      document.addEventListener('click', e => {
        const classLists = _.get(e.target, 'classList');
        if (classLists[0] === 'cdlc-user-anchor') {
          e.preventDefault();
          e.stopPropagation();
          const userId = _.replace(_.clone(classLists[1]), 'userId-', '');

          this.tools.navTo('UserProfilePage', null, {
            user_id: userId,
          });
        }
      });
    }, 250);
  }

  reset() {
    this.stop();
    this.text = '';
    this.taggedUsers = [];
    this.searchText = '';
    this.searchResults = {
      people: [],
      peopleMax: 20,
    };
  }

  async start(e: InputEvent, elementRef: Element) {
    const regexLetterNumbers = /^[a-zA-Z0-9]+$/;
    const userAnchor = /@[\w.]+/g;
    const inputValue = e.data;
    this.inputElement = elementRef;
    // this.log('start', inputValue, textAreaNativeElement);
    // const currentWord = await this.findCurrentWordTyping(e);
    // this.log('runTagging', inputValue, currentWord, this.searchText);
    this.log('start', inputValue);
    if (inputValue === '@') {
      this.log('handleTagging | setting TRUE');
      this.toggle = true;
      this.tools.logAnalyticsEvent(
        'trackCustom',
        'create-post-tagged-users-started'
      );
    }

    await this.handleTagging(e, inputValue);
  }

  stop() {
    this.toggle = false;
  }

  async handleTagging(e: InputEvent, inputValue: string) {
    if (this.toggle && inputValue !== ' ') {
      const { currentWordsArray } = await this.findCurrentWordsTyping();
      this.log('handleTagging | handle tagging ', currentWordsArray);
      this.searchText = currentWordsArray.join(' ');
      if (_.size(this.searchText)) await this.doSearch();
    }
  }

  async findCurrentWordsTyping(): Promise<{
    currentWord: string;
    currentWordsArray: string[];
    currentNode: any;
  }> {
    const { currentNode, text, cursorStart, cursorEnd } =
      this.findInputCursorAndValue();
    let wordsArray = this.tools.taggingWords(text);
    let currentWord = null;
    let currentWordsArray = [];
    let currentWordIndex = null;
    let skipCharacters = 0;
    if (wordsArray) {
      for (var i = 0; i < wordsArray.length; i++) {
        let wordStart = text.indexOf(wordsArray[i], skipCharacters);
        let wordEnd = wordStart + wordsArray[i].length;

        skipCharacters += wordEnd - wordStart;

        if (cursorStart >= wordStart && cursorEnd <= wordEnd) {
          currentWord = wordsArray[i];
          currentWordIndex = i;
          currentWordsArray = [currentWord];
          break;
        }
      }
    }

    if (_.first(currentWord) !== '@') {
      // max of three words
      let i = 1;
      let foundStart = false;
      do {
        const searchingIndex = currentWordIndex - i;
        const priorWord = wordsArray[searchingIndex];
        this.log('priorWord', currentWordIndex, i, priorWord);
        if (_.first(priorWord) === '@') foundStart = true;
        if (i === 4) {
          foundStart = true;
        }
        currentWordsArray.unshift(priorWord);
        i++;
      } while (i < 4 && !foundStart);
    }

    if (_.first(_.first(currentWordsArray)) !== '@') {
      this.stop();
    } else {
      _.replace(_.first(_.first(currentWordsArray)), '@', '');
    }
    this.log('findCurrentWordsTyping', currentWord, currentWordsArray);
    return {
      currentWord,
      currentWordsArray,
      currentNode,
    };
  }

  findInputCursorAndValue() {
    // // const cmKitInputPath = 'target.lastElementChild.children[1]';
    // if (_.get(e, 'target.localName') === 'cm-kit') {
    //   try {
    //     const target = _.get(e, 'target');
    //     this.lastTargetInput = target?.querySelector('textarea');
    //     // this.lastTargetInput = target?.getElementById('textarea');
    //   } catch (e) {}
    // } else if (_.get(e, 'target')) {
    //   this.lastTargetInput = e.target;
    // }
    const selection = window.getSelection();
    this.log('findInputCursorAndValue', this.inputElement, selection);

    // this.lastTargetInput = selection.anchorNode.parentNode;
    const value = {
      text: _.get(selection.anchorNode, 'textContent'),
      currentNode: selection.anchorNode,
      cursorStart: _.get(selection, 'baseOffset'),
      cursorEnd: _.get(selection, 'extentOffset'),
    };
    this.log('findInputCursorAndValue', value);
    return value;
  }

  async doSearch(forceSearch = false) {
    if (this.searchText.length === 0) {
      return (this.searchResults = {
        people: [],
        peopleMax: 20,
      });
    }

    this.isSearching = true;
    this.searchResults = {
      people: [],
      peopleMax: 20,
    };

    let st = this.searchText
        .replace(/“/g, '"')
        .replace(/”/g, '"')
        .replace(/@/g, ''),
      autocompleteOnly = false;

    if (forceSearch) {
      autocompleteOnly = false;
    } else {
      autocompleteOnly = _.size(st) <= 2;
    }

    this.log('starting user tagging search', st);

    this.searchId = _.uniqueId();

    await Promise.all(
      [!autocompleteOnly && this.peopleSearch(st, this.searchId)].filter(d => d)
    ).then(async () => {
      // Done
      this.log('results', this.searchResults);
      this.isSearching = false;
      await this.rankingResults();
    });
  }

  private async rankingResults() {
    // ranking people
    const exactMatchPeople = _.filter(this.searchResults.people, item => {
      const name = `${_.trim(item.firstName)} ${_.trim(item.lastName)}`;
      const nameLower = _.toLower(name);
      if (nameLower) {
        return (
          nameLower === _.trim(_.toLower(this.searchText)) ||
          _.trim(_.toLower(item.username)) ===
            _.trim(_.toLower(this.searchText))
        );
      }
    });

    _.map(exactMatchPeople, match => {
      _.remove(this.searchResults.people, people => {
        return people === match;
      });
      this.searchResults.people.unshift(match);
    });
  }

  private async peopleSearch(stringText: string, id: string) {
    this.log('peopleSearch', stringText);
    try {
      const { data } = await this.crudApi.get('/api/search/users', {
        text: stringText,
      });

      // still active
      if (this.searchId === id) {
        this.searchResults.people =
          _.each(
            data,
            u => (u.localUsername = _.replace(u.username, / +/g, '_'))
          ) || [];
        this.searchResults.peopleMax = 20;
        await this.loadMorePeople(0);
      }
    } catch (error) {
      this.log('Error getting people', error);
    } finally {
    }
  }

  async loadMorePeople(n = 5) {
    this.searchResults.peopleMax += n;
    this.log('loading more people');
    await Promise.all(
      _.map(this.searchResults?.people, (p, idx) => {
        if (idx < this.searchResults.peopleMax) {
          return this.crudApi
            .get(`/api/friends/${this.user.get('_id')}/${p._id}`)
            .then(res => {
              this.log('got friendship', res);
              if (res.data) {
                p.friendship = res.data;
              } else {
                p.noFriendship = true;
              }
            });
        }
      })
    );
    // this.isSearching = false;
  }

  replaceSpanMentions(inputString) {
    return inputString.replace(
      /<span[^>]*cdlc-tag="([^"]*)"[^>]*>([\s\S]*?)<\/span>/g,
      (_, cdlcTag, content) => {
        return cdlcTag.trim();
      }
    );
  }

  replaceMentionTags(inputString, taggedUsers, nolink = false) {
    const lodash = _;
    if (!_.size(taggedUsers) || !_.get(_.head(taggedUsers), '_id'))
      return inputString;
    // console.log('input taggedUsers', taggedUsers);
    return inputString.replace(/<@([^>]+)>/g, (_, userId) => {
      const person = lodash.find(taggedUsers, u => u?._id === userId);
      if (!lodash.get(person, '_id')) {
        return '';
      }
      if (nolink) {
        return `@${person.firstName} ${person.lastName}`;
      } else {
        return `<span class="cdlc-user-anchor userId-${person._id}" id="${person._id}"
            cdlc-tag="<@${person._id}>">
            @${person.firstName} ${person.lastName}
          </span>`;
      }
    });
  }

  async selectUser(person: any) {
    this.log('selectUser', person);
    // const element: any = this.inputElement;
    // element.focus();
    const inputValue = _.get(this.inputElement, 'innerHTML');
    const { currentNode, currentWordsArray } =
      await this.findCurrentWordsTyping();

    this.log('inputValue', inputValue);

    const replacedHTML = inputValue.replace(
      currentWordsArray.join(' '),
      `<span class="cdlc-user-anchor userId-${person._id}" id="${person._id}"
          cdlc-tag="<@${person._id}>">
          @${person.firstName} ${person.lastName}
        </span>&nbsp;`
    );

    try {
      currentNode.parentNode.innerHTML = replacedHTML;
    } catch (e) {}

    this.taggedUsers.push(person);
    this.log('selectUser', currentNode, this.taggedUsers);
    this.toggle = false;

    try {
      // this.inputElement.nativeElement.focus();
      const element = _.get(this, 'inputElement');
      this.log('focusing', element);
      this.log('element replaced', element, replacedHTML, replacedHTML);
      if (
        element.type !== 'textarea' &&
        element.getAttribute('contenteditable') === 'true'
      ) {
        element.focus();
        window.getSelection().selectAllChildren(element);
        window.getSelection().collapseToEnd();
      } else {
        // Place cursor at the end of text areas and input elements
        element.focus();
        element.select();
        window.getSelection().collapseToEnd();
      }
    } catch (e) {}

    this.tools.logAnalyticsEvent(
      'trackCustom',
      'create-post-tagged-users-selected',
      {
        username: _.get(person, 'username'),
        userId: _.get(person, '_id'),
      }
    );
  }
}



// WEBPACK FOOTER //
// ./src/providers/posts/tagging.provider.ts